/**
 * PWA Service - Handles Progressive Web App functionality
 * Provides utilities for installation, navigation, and app-like behavior
 */

export interface PWACapabilities {
  isInstallable: boolean;
  isInstalled: boolean;
  isStandalone: boolean;
  supportsShortcuts: boolean;
}

export class PWAService {
  private static instance: PWAService;
  private installPrompt: any = null;
  private capabilities: PWACapabilities;

  private constructor() {
    this.capabilities = this.detectCapabilities();
    this.setupEventListeners();
  }

  public static getInstance(): PWAService {
    if (!PWAService.instance) {
      PWAService.instance = new PWAService();
    }
    return PWAService.instance;
  }

  /**
   * Detect PWA capabilities of the current environment
   */
  private detectCapabilities(): PWACapabilities {
    // Handle test environment where window.matchMedia might not be available
    let isStandalone = false;
    
    try {
      if (typeof window !== 'undefined' && window.matchMedia) {
        isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                      (window.navigator as any).standalone ||
                      (typeof document !== 'undefined' && document.referrer.includes('android-app://'));
      }
    } catch (error) {
      // Fallback for test environments
      isStandalone = false;
    }

    const hasServiceWorker = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
    const hasBeforeInstallPrompt = typeof window !== 'undefined' && 'BeforeInstallPromptEvent' in window;

    return {
      isInstallable: hasServiceWorker && hasBeforeInstallPrompt,
      isInstalled: isStandalone,
      isStandalone,
      supportsShortcuts: hasServiceWorker
    };
  }

  /**
   * Set up PWA event listeners
   */
  private setupEventListeners(): void {
    // Handle install prompt
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.installPrompt = e;
      this.capabilities.isInstallable = true;
    });

    // Handle successful installation
    window.addEventListener('appinstalled', () => {
      this.capabilities.isInstalled = true;
      this.capabilities.isInstallable = false;
      this.installPrompt = null;
      console.log('PWA was installed successfully');
    });

    // Handle display mode changes
    try {
      if (typeof window !== 'undefined' && window.matchMedia) {
        window.matchMedia('(display-mode: standalone)').addEventListener('change', (e) => {
          this.capabilities.isStandalone = e.matches;
        });
      }
    } catch (error) {
      // Ignore in test environments
    }
  }

  /**
   * Get current PWA capabilities
   */
  public getCapabilities(): PWACapabilities {
    return { ...this.capabilities };
  }

  /**
   * Show the install prompt if available
   */
  public async showInstallPrompt(): Promise<boolean> {
    if (!this.installPrompt) {
      return false;
    }

    try {
      this.installPrompt.prompt();
      const { outcome } = await this.installPrompt.userChoice;
      
      if (outcome === 'accepted') {
        this.capabilities.isInstallable = false;
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error showing install prompt:', error);
      return false;
    }
  }

  /**
   * Check if app was launched from a shortcut
   */
  public getLaunchMode(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    
    // Also check session storage for launch mode
    const sessionMode = sessionStorage.getItem('launchMode');
    if (sessionMode) {
      sessionStorage.removeItem('launchMode');
      return sessionMode;
    }
    
    return mode;
  }

  /**
   * Handle app-like navigation behavior
   */
  public setupAppNavigation(): void {
    // Prevent default browser navigation behaviors in standalone mode
    if (this.capabilities.isStandalone) {
      // Prevent pull-to-refresh
      document.body.style.overscrollBehavior = 'none';
      
      // Prevent zoom on double tap
      let lastTouchEnd = 0;
      document.addEventListener('touchend', (event) => {
        const now = (new Date()).getTime();
        if (now - lastTouchEnd <= 300) {
          event.preventDefault();
        }
        lastTouchEnd = now;
      }, false);
      
      // Handle back button behavior
      window.addEventListener('popstate', (e) => {
        // Custom back button handling for app-like behavior
        if (window.history.length <= 1) {
          // If no history, prevent going back to browser
          e.preventDefault();
          window.history.pushState(null, '', window.location.href);
        }
      });

      // Add initial history state
      if (window.history.length === 1) {
        window.history.pushState(null, '', window.location.href);
      }

      // Prevent context menu on long press (iOS)
      document.addEventListener('contextmenu', (e) => {
        e.preventDefault();
      });

      // Handle viewport changes for better app-like behavior
      this.handleViewportChanges();
    }

    // Always prevent text selection for app-like feel
    this.setupAppLikeInteractions();
  }

  /**
   * Handle viewport changes for responsive behavior
   */
  private handleViewportChanges(): void {
    // Handle orientation changes
    window.addEventListener('orientationchange', () => {
      // Force viewport recalculation after orientation change
      setTimeout(() => {
        window.scrollTo(0, 0);
        this.updateViewportHeight();
      }, 100);
    });

    // Handle resize events
    window.addEventListener('resize', () => {
      this.updateViewportHeight();
    });

    // Initial viewport setup
    this.updateViewportHeight();
  }

  /**
   * Update viewport height for mobile browsers
   */
  private updateViewportHeight(): void {
    // Set CSS custom property for actual viewport height
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  /**
   * Setup app-like interactions
   */
  private setupAppLikeInteractions(): void {
    // Prevent text selection on UI elements
    document.body.style.webkitUserSelect = 'none';
    document.body.style.userSelect = 'none';
    
    // Allow text selection in input fields and content areas
    const selectableElements = document.querySelectorAll('input, textarea, [contenteditable], .selectable');
    selectableElements.forEach(element => {
      (element as HTMLElement).style.webkitUserSelect = 'text';
      (element as HTMLElement).style.userSelect = 'text';
    });

    // Prevent drag on images and other elements
    document.addEventListener('dragstart', (e) => {
      if (e.target instanceof HTMLImageElement || 
          e.target instanceof HTMLAnchorElement) {
        e.preventDefault();
      }
    });

    // Add touch feedback for interactive elements
    this.addTouchFeedback();
  }

  /**
   * Add touch feedback for better mobile experience
   */
  private addTouchFeedback(): void {
    try {
      if (typeof document !== 'undefined') {
        const style = document.createElement('style');
        style.textContent = `
          .touch-feedback {
            position: relative;
            overflow: hidden;
          }
          
          .touch-feedback::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0;
            height: 0;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.3);
            transform: translate(-50%, -50%);
            transition: width 0.3s ease, height 0.3s ease;
            pointer-events: none;
          }
          
          .touch-feedback:active::after {
            width: 200px;
            height: 200px;
          }
        `;
        document.head.appendChild(style);

        // Add touch feedback class to interactive elements
        document.addEventListener('DOMContentLoaded', () => {
          const interactiveElements = document.querySelectorAll('button, .button, [role="button"], .interactive');
          interactiveElements.forEach(element => {
            element.classList.add('touch-feedback');
          });
        });
      }
    } catch (error) {
      // Ignore DOM errors in test environments
    }
  }

  /**
   * Get app theme based on system preferences and PWA context
   */
  public getAppTheme(): 'light' | 'dark' {
    try {
      if (typeof window !== 'undefined' && window.matchMedia && 
          window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    } catch (error) {
      // Fallback for test environments
    }
    return 'light';
  }

  /**
   * Update theme color dynamically
   */
  public updateThemeColor(color: string): void {
    // Update meta theme-color
    let themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeColorMeta) {
      themeColorMeta = document.createElement('meta');
      themeColorMeta.setAttribute('name', 'theme-color');
      document.head.appendChild(themeColorMeta);
    }
    themeColorMeta.setAttribute('content', color);

    // Update Microsoft tile color
    let tileColorMeta = document.querySelector('meta[name="msapplication-TileColor"]');
    if (!tileColorMeta) {
      tileColorMeta = document.createElement('meta');
      tileColorMeta.setAttribute('name', 'msapplication-TileColor');
      document.head.appendChild(tileColorMeta);
    }
    tileColorMeta.setAttribute('content', color);
  }

  /**
   * Check if app needs update
   */
  public checkForUpdates(): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator && navigator.serviceWorker.getRegistration) {
          navigator.serviceWorker.getRegistration().then((registration) => {
            if (registration && registration.update) {
              registration.update().then(() => {
                resolve(registration.waiting !== null);
              }).catch(() => resolve(false));
            } else {
              resolve(false);
            }
          }).catch(() => resolve(false));
        } else {
          resolve(false);
        }
      } catch (error) {
        resolve(false);
      }
    });
  }

  /**
   * Apply app update
   */
  public applyUpdate(): void {
    try {
      if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator && navigator.serviceWorker.getRegistration) {
        navigator.serviceWorker.getRegistration().then((registration) => {
          if (registration && registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            if (typeof window !== 'undefined' && window.location) {
              window.location.reload();
            }
          }
        }).catch(() => {
          // Ignore errors in test environments
        });
      }
    } catch (error) {
      // Ignore errors in test environments
    }
  }

  /**
   * Share content using Web Share API or fallback
   */
  public async shareContent(shareData: {
    title?: string;
    text?: string;
    url?: string;
  }): Promise<boolean> {
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return true;
      } catch (error) {
        console.error('Error sharing:', error);
      }
    }
    
    // Fallback to clipboard
    if (navigator.clipboard && shareData.url) {
      try {
        await navigator.clipboard.writeText(shareData.url);
        return true;
      } catch (error) {
        console.error('Error copying to clipboard:', error);
      }
    }
    
    return false;
  }
}

// Export singleton instance (lazy initialization for tests)
let pwaServiceInstance: PWAService | null = null;

export const pwaService = {
  get instance(): PWAService {
    if (!pwaServiceInstance) {
      pwaServiceInstance = PWAService.getInstance();
    }
    return pwaServiceInstance;
  }
};

// For backward compatibility
export const getPWAService = () => pwaService.instance;